import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import AlignmentExportDropdown, {
  AlignmentExportDropdownProps,
} from 'analyses/alignment/AlignmentExportDropdown';
import NameInput from 'analyses/alignment/NameInput';
import RealignButton from 'analyses/alignment/RealignButton';
import BufferOptionsItem from 'buffers/BufferOptionsItem';
import DisplayOptions from 'components/DisplayOptions';
import {VisibilityOptions} from 'components/VisibilityOptions';
import {DNAAlignmentKind} from 'constants/AlignmentConstants';
import SearchButton from 'molbio/maps/options/SearchButton';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ButtonGroup from 'ui-library/buttons/ButtonGroup';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

export type AlignmentTabMenuProps = {
  addComment: () => void;
  alignmentType: DNAAlignmentKind;
  allowFeatureEdits: boolean;
  allowResidueEdits: boolean;
  goToMismatch: (direction: 'next' | 'previous') => void;
  onToggleSearch: () => void;
  openRealignModal: () => void;
  sortRows: (sortByPosition: boolean) => void;
  updateName: (name: string) => Promise<void>;
  visibilityOptions: VisibilityOptions;
} & AlignmentExportDropdownProps;

export default function AlignmentTabMenu({
  addComment,
  alignmentApiId,
  alignmentType,
  allowFeatureEdits,
  allowResidueEdits,
  analysisName,
  attachedFileCount,
  getClustalFormatData,
  getFastaFormatData,
  goToMismatch,
  loaded,
  onToggleSearch,
  openPDFFormattedPreview,
  openPDFSnapshotPreview,
  openRealignModal,
  selectionLength,
  sequenceEditURL,
  sortRows,
  updateName,
  visibilityOptions,
}: AlignmentTabMenuProps): JSX.Element {
  return (
    <div
      className="buffer-options-container sequence-alignment-menu"
      {...getTestProps({component: 'AlignmentTabMenu', element: 'root'})}
    >
      <BufferOptionsItem>
        <NameInput
          allowFeatureEdits={allowFeatureEdits}
          analysisName={analysisName}
          loaded={loaded}
          updateName={updateName}
        />
      </BufferOptionsItem>
      <BufferOptionsItem className={css(styles.expander)} />
      <BufferOptionsItem>
        <span className={css(styles.mismatchLabel)}>Find Mismatches</span>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.MINI}
          className="alignment-previous-mismatch"
          icon="chevron-left"
          onClick={() => {
            goToMismatch('previous');
          }}
          disabled={!loaded}
        />
        <span className={css(styles.mismatchSpacer)} />
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.MINI}
          className="alignment-next-mismatch"
          icon="chevron"
          onClick={() => {
            goToMismatch('next');
          }}
          disabled={!loaded}
        />
      </BufferOptionsItem>
      <BufferOptionsItem className="u-vertical-center-items">
        <ButtonGroup>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.MINI}
            className="add-alignment-comment"
            text="Add Comment"
            disabled={!loaded}
            onClick={() => {
              addComment();
            }}
          />
          <Dropdown
            menu={
              <Menu width={MenuWidth.SMALL}>
                <Option
                  onSelect={() => {
                    sortRows(false);
                  }}
                >
                  Name
                </Option>
                <Option
                  onSelect={() => {
                    sortRows(true);
                  }}
                >
                  Position
                </Option>
              </Menu>
            }
          >
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.MINI}
              isDropdown={true}
              text="Sort By"
              disabled={!loaded}
            />
          </Dropdown>
          <AlignmentExportDropdown
            selectionLength={selectionLength}
            openPDFSnapshotPreview={openPDFSnapshotPreview}
            openPDFFormattedPreview={openPDFFormattedPreview}
            getClustalFormatData={getClustalFormatData}
            getFastaFormatData={getFastaFormatData}
            analysisName={analysisName}
            loaded={loaded}
            alignmentApiId={alignmentApiId}
            attachedFileCount={attachedFileCount}
            sequenceEditURL={sequenceEditURL}
          />
          <DisplayOptions options={visibilityOptions} isWide={true} />
          <SearchButton onToggleSearch={onToggleSearch} />
        </ButtonGroup>
      </BufferOptionsItem>
      <BufferOptionsItem className="u-vertical-center-items">
        <RealignButton
          alignmentType={alignmentType}
          allowFeatureEdits={allowFeatureEdits}
          allowResidueEdits={allowResidueEdits}
          loaded={loaded}
          openRealignModal={openRealignModal}
        />
      </BufferOptionsItem>
    </div>
  );
}

const styles = StyleSheet.create({
  mismatchLabel: {
    marginRight: '5px',
  },
  mismatchSpacer: {
    marginRight: '2px',
  },
  expander: {
    flex: 1,
  },
});
